/**
 * GMA-1619 (CompassCare Web 4/14 — Alerts queue + critical fall response) —
 * pure model.
 *
 * The emergency-response queue answers "who needs help, how urgently, and where
 * is this in the response lifecycle." This module holds the UI-free logic the
 * Alerts screen renders:
 *   - the response state machine New → Acknowledged → Dispatched → Resolved,
 *     recording an actor + timestamp at every transition so the Audit Log can
 *     reconstruct who did what, when (GMA-1619 AC);
 *   - queue ordering (severity → age, with resolved alerts pushed to the bottom);
 *   - the HARD language requirement — the fixed response-detail section headings
 *     and a guard that rejects casual/unsafe phrasing ("meet up", etc.).
 *
 * Fail-safe like the rest of the facelift: a malformed alert never throws, an
 * invalid transition is a no-op (state/history can't be corrupted), and sorting
 * is non-mutating. Severity vocabulary + age come from the shared model.
 */
import { sortAlertsBySeverityThenAge } from "./overviewModel";

// Response lifecycle, in order. `resolved` is terminal.
export const ALERT_STATES = Object.freeze([
  "new",
  "acknowledged",
  "dispatched",
  "resolved",
]);

export const ALERT_STATE_LABELS = Object.freeze({
  new: "New",
  acknowledged: "Acknowledged",
  dispatched: "Dispatched",
  resolved: "Resolved",
});

const DEFAULT_STATE = "new";

// Valid forward transitions. A NEW alert must be acknowledged before it can be
// closed — "silence is not reassurance", so nothing is dismissed without a
// responder first looking at it. Once acknowledged, an alert may be resolved
// early (e.g. a false alarm cleared before dispatch). `resolved` is terminal.
const TRANSITIONS = Object.freeze({
  new: Object.freeze(["acknowledged"]),
  acknowledged: Object.freeze(["dispatched", "resolved"]),
  dispatched: Object.freeze(["resolved"]),
  resolved: Object.freeze([]),
});

// HARD requirement (GMA-1619): the critical-fall response detail uses exactly
// these section headings, in this order. The consuming component reads this list
// so the copy can never drift into casual phrasing; a contract test pins it.
export const RESPONSE_SECTION_HEADINGS = Object.freeze([
  "Response Details",
  "Access Instructions",
  "Arrival Guidance",
  "Responder Notes",
  "Client Safety Instructions",
]);

// Casual / unsafe phrasing that must never appear in responder-facing copy.
// "Silence is not reassurance" — the tone is clinical, not social. Matched with
// word boundaries (not a bare substring) so a single word like "grab" is caught
// regardless of trailing punctuation ("grab,", "grab.", "grab!") while not
// firing on longer words that merely contain it ("grabbing"). Multi-word phrases
// tolerate variable inner whitespace ("meet  up").
const FORBIDDEN_PHRASES = Object.freeze([
  "meet up",
  "hang out",
  "catch up",
  "grab",
  "no worries",
  "chill",
]);

const FORBIDDEN_PATTERNS = FORBIDDEN_PHRASES.map(
  (phrase) => new RegExp(`\\b${phrase.replace(/\s+/gu, "\\s+")}\\b`, "iu"),
);

/**
 * Current lifecycle state of an alert, defaulting unknown/missing to "new".
 * @param {*} alert
 * @returns {string} one of ALERT_STATES
 */
export function alertState(alert) {
  const s = alert && typeof alert === "object" ? alert.state : undefined;
  return Object.prototype.hasOwnProperty.call(TRANSITIONS, s) ? s : DEFAULT_STATE;
}

/**
 * Whether a state transition is permitted.
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
export function canTransition(from, to) {
  const allowed = Object.prototype.hasOwnProperty.call(TRANSITIONS, from)
    ? TRANSITIONS[from]
    : null;
  return Array.isArray(allowed) && allowed.includes(to);
}

/**
 * Valid next states from an alert's current state (for rendering the action
 * bar). Terminal `resolved` yields []. Fail-safe for a malformed alert.
 * @param {*} alert
 * @returns {string[]} a copy of the allowed next states
 */
export function nextStates(alert) {
  return TRANSITIONS[alertState(alert)].slice();
}

/**
 * Advance an alert to a new state, recording {state, actor, at} in its history
 * for the Audit Log. Pure + non-mutating: returns a NEW alert. An invalid
 * transition applies no change — it returns a normalized copy of the alert with
 * `state` set to its current (validated) state and `history` copied (an unknown
 * incoming state is normalized to the default) — so state/history can never be
 * corrupted by a stray call. Fail-safe for a malformed alert.
 *
 * @param {object} alert
 * @param {string} to target state
 * @param {string} actor who performed the transition (user id / name)
 * @param {number} at epoch-ms timestamp of the transition
 * @returns {object} the resulting alert
 */
export function applyTransition(alert, to, actor, at) {
  const base = alert && typeof alert === "object" && !Array.isArray(alert) ? alert : {};
  const from = alertState(base);
  const history = Array.isArray(base.history) ? base.history : [];
  if (!canTransition(from, to)) {
    // No-op: return a normalized copy without mutating or corrupting anything.
    return { ...base, state: from, history: history.slice() };
  }
  return {
    ...base,
    state: to,
    history: [...history, { state: to, actor, at }],
  };
}

/**
 * Order alerts for the response queue: active alerts by severity then age (via
 * the shared overviewModel), with resolved alerts pushed to the bottom (handled,
 * lowest priority). Pure, non-mutating, fail-safe for malformed input.
 *
 * @param {Array} alerts
 * @param {number} [now=Date.now()]
 * @returns {Array} a new, ordered array
 */
export function sortAlertsForQueue(alerts, now = Date.now()) {
  if (!Array.isArray(alerts)) return [];
  const rows = alerts.filter(
    (a) => a !== null && typeof a === "object" && !Array.isArray(a),
  );
  const active = rows.filter((a) => alertState(a) !== "resolved");
  const resolved = rows.filter((a) => alertState(a) === "resolved");
  return [
    ...sortAlertsBySeverityThenAge(active, now),
    ...sortAlertsBySeverityThenAge(resolved, now),
  ];
}

/**
 * Guard for the HARD language requirement: does responder-facing copy contain
 * casual/unsafe phrasing? Case-insensitive; fail-safe for non-string input.
 * @param {*} text
 * @returns {boolean}
 */
export function containsForbiddenPhrasing(text) {
  if (typeof text !== "string") return false;
  return FORBIDDEN_PATTERNS.some((pattern) => pattern.test(text));
}
